// Typed helpers for the inbox views (docs/design/inbox-views.md): the cached label list, the
// "Search all of Gmail" passthrough, the cardless thread actions (approve) and the work item they
// queue (status, receipts, cancel). Nothing here touches Gmail: `runAction` asks the server for an
// approval bound to the exact thread and delta, and the executor lane runs it. URL builders are
// pure so they can be unit-tested.
import type {
  GmailSearchResponse,
  LabelView,
  ReceiptView,
  ThreadActionRequest,
  ThreadActionResult,
  WorkView,
} from "@shared/api";
import { api } from "./api";

const enc = encodeURIComponent;

/** One page of Gmail hits is plenty; a second page is a narrower query (before:, from:). */
export const GMAIL_SEARCH_LIMIT = 25;

function query(params: Record<string, string | number | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}

export const inboxUrls = {
  labels: (accountId: string, refresh = false) =>
    `/api/mail/labels${query({ accountId, refresh: refresh ? 1 : undefined })}`,
  gmailSearch: (accountId: string, q: string, limit = GMAIL_SEARCH_LIMIT) =>
    `/api/mail/search${query({ scope: "gmail", accountId, q: q.trim(), limit })}`,
  action: (threadKey: string) => `/api/mail/threads/${enc(threadKey)}/actions`,
  work: (workId: string) => `/api/work/${enc(workId)}`,
  workReceipts: (workId: string) => `/api/work/${enc(workId)}/receipts`,
  workCancel: (workId: string) => `/api/work/${enc(workId)}/cancel`,
};

/** The request as the server wants it: only the fields the kind takes, ids deduplicated. */
export function cleanRequest(request: ThreadActionRequest): ThreadActionRequest {
  if (request.kind !== "gmail.label") return { kind: request.kind };
  const dedupe = (ids: readonly string[] | undefined) => [...new Set(ids ?? [])];
  return {
    kind: "gmail.label",
    addLabelIds: dedupe(request.addLabelIds),
    removeLabelIds: dedupe(request.removeLabelIds),
  };
}

export const inboxApi = {
  labels: (accountId: string, refresh = false, signal?: AbortSignal) =>
    api<LabelView[]>(inboxUrls.labels(accountId, refresh), { signal }),
  gmailSearch: (accountId: string, q: string, signal?: AbortSignal) =>
    api<GmailSearchResponse>(inboxUrls.gmailSearch(accountId, q), { signal }),
  runAction: (threadKey: string, request: ThreadActionRequest) =>
    api<ThreadActionResult>(inboxUrls.action(threadKey), {
      method: "POST",
      body: cleanRequest(request),
    }),
  work: (workId: string, signal?: AbortSignal) => api<WorkView>(inboxUrls.work(workId), { signal }),
  workReceipts: (workId: string, signal?: AbortSignal) =>
    api<ReceiptView[]>(inboxUrls.workReceipts(workId), { signal }),
  cancelWork: (workId: string) => api<WorkView>(inboxUrls.workCancel(workId), { method: "POST" }),
};
